import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { MapPin, Briefcase, TrendingUp, Check } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { PublicLayout } from "@/components/layout/PublicLayout";
import { Loader } from "@/components/shared/Loader";
import { useAuth } from "@/context/AuthContext";

export default function JobDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [job, setJob] = useState(null);

  useEffect(() => {
    api.get(`/jobs/${id}`).then((r) => setJob(r.data)).catch(() => {});
  }, [id]);

  if (!job) return <PublicLayout><Loader /></PublicLayout>;

  const apply = () => {
    if (!user) {
      toast.info("Please sign in or register to apply.");
      navigate(`/login?redirect=/careers/${id}/apply`);
      return;
    }
    if (user.role === "admin") {
      toast.error("Admins cannot apply. Use a candidate account.");
      return;
    }
    navigate(`/careers/${id}/apply`);
  };

  return (
    <PublicLayout>
      <div className="mx-auto max-w-5xl px-6 pb-24 pt-36 lg:px-10">
        <div className="mb-3 flex items-center gap-3">
          <span className="rounded-full bg-gold/10 px-3 py-1 font-mono text-[10px] uppercase tracking-widest text-gold">{job.department}</span>
          <span className="font-mono text-[10px] uppercase tracking-widest text-[var(--aur-text-2)]">{job.code}</span>
        </div>
        <h1 className="font-serif text-4xl tracking-tight sm:text-5xl">{job.title}</h1>
        <div className="mt-5 flex flex-wrap gap-5 font-sans text-sm text-[var(--aur-text-2)]">
          <span className="flex items-center gap-2"><MapPin size={15} className="text-gold" />{job.location}</span>
          <span className="flex items-center gap-2"><Briefcase size={15} className="text-gold" />{job.employment_type}</span>
          <span className="flex items-center gap-2"><TrendingUp size={15} className="text-gold" />{job.level}</span>
        </div>

        <div className="mt-12 grid gap-12 md:grid-cols-3">
          <div className="space-y-10 md:col-span-2">
            <div>
              <p className="eyebrow">Overview</p>
              <p className="mt-4 font-sans text-base leading-relaxed text-[var(--aur-text-2)]">{job.summary}</p>
            </div>
            <div>
              <p className="eyebrow">Responsibilities</p>
              <ul className="mt-4 space-y-3">
                {job.responsibilities.map((r, i) => (
                  <li key={i} className="flex gap-3 font-sans text-sm text-[var(--aur-text)]"><Check size={16} className="mt-0.5 shrink-0 text-gold" />{r}</li>
                ))}
              </ul>
            </div>
            <div>
              <p className="eyebrow">What we look for</p>
              <ul className="mt-4 space-y-3">
                {job.requirements.map((r, i) => (
                  <li key={i} className="flex gap-3 font-sans text-sm text-[var(--aur-text)]"><Check size={16} className="mt-0.5 shrink-0 text-gold" />{r}</li>
                ))}
              </ul>
            </div>
          </div>

          {/* Apply panel */}
          <div className="h-fit rounded-lg border border-[var(--aur-border)] bg-[var(--aur-card)] p-6 md:sticky md:top-28">
            <p className="eyebrow">Apply</p>
            {job.salary_range && <p className="mt-3 font-serif text-lg text-gold">{job.salary_range}</p>}
            <p className="mt-4 font-sans text-sm leading-relaxed text-[var(--aur-text-2)]">
              A guided application form adapts to your applicant type — Singapore Citizen, PR, existing pass holder, or international.
            </p>
            <button
              onClick={apply}
              data-testid="job-apply-btn"
              className="mt-5 w-full rounded-full bg-gold px-6 py-3 font-sans text-xs font-semibold uppercase tracking-widest text-[var(--aur-bg)] transition-all hover:bg-gold-bright"
            >
              {user ? "Apply for this role" : "Sign in to Apply"}
            </button>
            <p className="mt-3 font-sans text-[11px] leading-relaxed text-[var(--aur-text-2)]">
              You'll upload your CV, passport (if applicable) and certificates within the form.
            </p>
          </div>
        </div>
      </div>
    </PublicLayout>
  );
}
